
function ensureBBButtonLifecycle() {
    if (typeof window === 'undefined') {
        return null;
    }
    if (window.BBButtonLifecycle) {
        return window.BBButtonLifecycle;
    }

    const state = new WeakMap();

    const isInputButton = (button) => button && /^(INPUT)$/i.test(button.tagName);

    const toElement = (target) => {
        if (!target) {
            return null;
        }
        if (typeof target === 'string') {
            return document.querySelector(target);
        }
        if (target.jquery) {
            return target[0];
        }
        return target;
    };

    const original = (button) => {
        if (!state.has(button)) {
            state.set(button, {
                html: isInputButton(button) ? null : button.innerHTML,
                value: isInputButton(button) ? button.value : null,
                disabled: !!button.disabled,
                ariaBusy: button.getAttribute('aria-busy'),
            });
        }
        return state.get(button);
    };

    const setButtonContent = (button, icon, label, busy) => {
        if (!button) {
            return;
        }
        const text = label || button.getAttribute('data-default-label') || button.textContent.trim() || button.value || 'Save';
        if (isInputButton(button)) {
            button.value = text;
            return;
        }
        button.replaceChildren();
        if (busy) {
            const spinner = document.createElement('span');
            spinner.className = 'bb-spin';
            spinner.setAttribute('aria-hidden', 'true');
            button.appendChild(spinner);
        } else if (icon) {
            const iconSpan = document.createElement('span');
            iconSpan.className = 'bb-btn__state-icon';
            iconSpan.setAttribute('aria-hidden', 'true');
            iconSpan.textContent = icon;
            button.appendChild(iconSpan);
        }
        const labelSpan = document.createElement('span');
        labelSpan.className = 'bb-btn__label';
        labelSpan.textContent = text;
        button.appendChild(labelSpan);
    };

    const feedbackFor = (button, feedback) => {
        const explicit = toElement(feedback);
        if (explicit) {
            return explicit;
        }
        const scope = button && button.closest('[data-bb-action-scope], .component-data-save, .modal-body, .modal-content, form');
        return scope ? scope.querySelector('[data-bb-action-feedback], .bb-action-feedback') : null;
    };

    const setFeedback = (button, options = {}) => {
        const feedback = feedbackFor(button, options.feedback);
        if (!feedback) {
            return;
        }
        feedback.replaceChildren();
        feedback.className = 'bb-action-feedback';
        if (feedback.hasAttribute('data-extra-class')) {
            feedback.className += ` ${feedback.getAttribute('data-extra-class')}`;
        }
        if (!options.state) {
            return;
        }
        const iconSpan = document.createElement('span');
        iconSpan.className = 'bb-action-feedback__icon';
        iconSpan.setAttribute('aria-hidden', 'true');
        iconSpan.textContent = options.icon || (options.state === 'success' ? '?' : '!');
        const textSpan = document.createElement('span');
        textSpan.textContent = options.message || (options.state === 'success' ? 'Saved successfully.' : 'Something went wrong.');
        feedback.appendChild(iconSpan);
        feedback.appendChild(textSpan);
        feedback.classList.add('is-visible', options.state === 'success' ? 'is-success' : 'is-error');
    };

    const start = (target, options = {}) => {
        const button = toElement(target);
        if (!button) {
            return Promise.resolve();
        }
        original(button);
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.classList.remove('is-success', 'is-error');
        button.classList.add('is-loading');
        setButtonContent(button, null, options.label || 'Saving...', true);
        setFeedback(button, { feedback: options.feedback });
        return Promise.resolve(button);
    };

    const reset = (target, options = {}) => {
        const button = toElement(target);
        if (!button) {
            return Promise.resolve();
        }
        const old = state.get(button);
        button.classList.remove('is-loading', 'is-success', 'is-error');
        if (options.disabled === true) {
            button.disabled = true;
        } else if (options.disabled === false) {
            button.disabled = false;
        } else if (old) {
            button.disabled = old.disabled;
        } else {
            button.disabled = false;
        }
        if (old && old.ariaBusy === null) {
            button.removeAttribute('aria-busy');
        } else if (old && old.ariaBusy) {
            button.setAttribute('aria-busy', old.ariaBusy);
        } else {
            button.removeAttribute('aria-busy');
        }
        if (options.label) {
            setButtonContent(button, null, options.label, false);
        } else if (old) {
            if (isInputButton(button)) {
                button.value = old.value || 'Save';
            } else {
                button.innerHTML = old.html;
            }
        }
        return Promise.resolve(button);
    };

    const finish = (target, outcome, options = {}) => {
        const button = toElement(target);
        if (!button) {
            return Promise.resolve();
        }
        const success = outcome === 'success';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading', success ? 'is-error' : 'is-success');
        button.classList.add(success ? 'is-success' : 'is-error');
        setButtonContent(button, success ? '?' : '!', options.label || (success ? 'Saved' : 'Try again'), false);
        setFeedback(button, {
            feedback: options.feedback,
            state: success ? 'success' : 'error',
            icon: success ? '?' : '!',
            message: options.message || (success ? 'Saved successfully.' : 'Something went wrong.'),
        });
        if (Number.isFinite(options.resetAfter)) {
            return new Promise((resolve) => {
                window.setTimeout(() => {
                    reset(button, {
                        label: options.resetLabel,
                        disabled: options.disabledAfterReset === true,
                    }).then(resolve);
                }, options.resetAfter);
            });
        }
        return Promise.resolve(button);
    };

    window.BBButtonLifecycle = {
        start,
        reset,
        success: (target, options = {}) => finish(target, 'success', options),
        error: (target, options = {}) => finish(target, 'error', options),
        setFeedback,
        bindDemo: () => {},
    };

    return window.BBButtonLifecycle;
}

export class SaveButton{

    parentSelector

    constructor(parentSelector=null, btnSelector=null){
        this.buttonDotsInterval;
        this.i = 0;
        this.parentSelector = parentSelector;
        this.button = document.querySelector(`${parentSelector ?? ''} ${btnSelector ?? 'button[data-save]'}`);
        if (!this.button && !btnSelector) {
            this.button = document.querySelector('#save-settings');
        }
        this.defaultText = this.button?.getAttribute('data-default-label') || this.button?.textContent?.trim() || this.button?.value || 'Save';
    }

    get lifecycle(){
        return ensureBBButtonLifecycle();
    }

    get feedback(){
        const scoped = this.button?.closest('[data-bb-action-scope], .component-data-save, .modal-body, .modal-content, form');
        return scoped?.querySelector?.('[data-bb-action-feedback], .bb-action-feedback') || null;
    }

    /**
     * Отображает процесс выполнения операции
     *
     * @returns Promise
     */
    startLoading(message='Saving') {
        if (!this.button) {
            return Promise.resolve();
        }
        if (this.button) {
            this.hideMessages();
            if (this.lifecycle) {
                return this.lifecycle.start(this.button, {
                    label: `${message}...`,
                    feedback: this.feedback
                });
            }
            this.button.setAttribute('disabled', '');
        }

        return new Promise( () => {
            this.buttonDotsInterval = setInterval(() => {
                try {
                    this.button.innerHTML = message + Array((++this.i % 4) + 1).join('.');
                } catch (error) {
                    console.log(error);
                }
            }, 500)
        });
    }

    /**
     *  Останавливает процесс отображение процесса загрузки.
     *
     * @param {boolean} disabled флаг блокирования кнопки: для всплывающих окон true, для всего остального false
     * @returns Promise
     */
    stopLoading(disabled=false,btnText='Save') {
        if (!this.button) {
            clearInterval(this.buttonDotsInterval);
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                clearInterval(this.buttonDotsInterval);
                try {
                    if (this.lifecycle) {
                        this.lifecycle.reset(this.button, {
                            label: btnText || this.defaultText,
                            disabled: disabled === true
                        }).then(resolve);
                        return;
                    }
                    if (!disabled) {
                        this.button.removeAttribute('disabled');
                    }
                    this.button.innerHTML = btnText;
                } catch (error) {
                    console.log(error);
                }

                resolve();
            }, 450)
        });
    }

    /**
     * Отображает сообщение об успешной операции.
     *
     * @returns Promise
     */
    showSuccess() {
        if (this.lifecycle && this.button) {
            // holdFor, not resetAfter: hold the green Saved state until whatever comes next
            // (these flows reload the page or close the modal). With resetAfter the button
            // reverted to its red idle label ~1s in — a full second BEFORE the reload — so a
            // successful save read as "nothing happened".
            // Measured: drafts/modal-verify/fm-timeline.cjs.
            return this.lifecycle.success(this.button, {
                label: 'Saved',
                holdFor: 1400,
                feedback: this.feedback,
                message: 'Successfully saved.'
            });
        }
        return new Promise((resolve) => {
            setTimeout(() => {
                try {
                    $(`${this.parentSelector ?? ''} .component-data-save .text-success`).removeClass('d-none')
                } catch (error) {}
                setTimeout(() => {
                    try {
                        $(`${this.parentSelector ?? ''} .component-data-save .text-success`).addClass('d-none');
                    } catch (error) {}
                    resolve();
                }, 1000)
            }, 500)
        });
    }

    /**
     * Отображает сообщение об ошибке при выполнении операции
     *
     * @returns Promise
     */
    showError(message=null) {
        if (this.lifecycle && this.button) {
            let errorMessage = message || 'Something went wrong.';
            const feedback = this.feedback;
            try {
                const legacyMessage = document.querySelector(`${this.parentSelector ?? ''} .component-data-save .error-message`);
                if (legacyMessage) {
                    if (message) {
                        legacyMessage.textContent = message;
                    }
                    const legacyText = legacyMessage.textContent?.trim();
                    if (!message && legacyText) {
                        errorMessage = legacyText;
                    }
                    if (feedback) {
                        legacyMessage.classList.add('d-none');
                    } else if (message) {
                        legacyMessage.classList.remove('d-none');
                    }
                }
            } catch (error) {}
            return this.lifecycle.error(this.button, {
                label: 'Try again',
                // hold the red Try again state too — an error must stay readable next to
                // its message instead of quietly reverting to the idle label.
                holdFor: 1500,
                feedback,
                message: errorMessage
            });
        }
        return new Promise( (resolve) => {
            setTimeout(() => {
                try {
                    document.querySelector(`${this.parentSelector ?? ''} .component-data-save .text-danger`)?.classList?.toggle('d-none');
                } catch (error) {}
                setTimeout(() => {
                    try {
                        document.querySelector(`${this.parentSelector ?? ''} .component-data-save .error-message`)?.classList?.remove('d-none');
                        document.querySelector(`${this.parentSelector ?? ''} .component-data-save .text-danger`)?.classList?.toggle('d-none');
                        if(message)
                            document.querySelector(`${this.parentSelector ?? ''} .component-data-save .error-message`).textContent = message
                    } catch (error) {console.log(error)}
                    resolve();
                }, 1500)
            }, 500)
        });
    }

    hideMessages(){
        $(`${this.parentSelector ?? ''} .component-data-save .text-danger`).addClass('d-none')
        $(`${this.parentSelector ?? ''} .component-data-save .text-success`).addClass('d-none')
        $(`${this.parentSelector ?? ''} .component-data-save .error-message`).addClass('d-none')
        if (this.lifecycle && this.button) {
            this.lifecycle.setFeedback(this.button, { feedback: this.feedback });
        }
    }
}
